export const QUESTIONS = [
  // ── Domain 1: Enrollment Capacity ─────────────────────────────────────────
  { key: "q_recruit_month",    no: "1",  section: 1, sectionTitle: "Enrollment Capacity",
    text: "How many patients could you recruit per month?", type: "numeric", hasSpecify: false },
  { key: "q_recruit_year",     no: "2",  section: 1,
    text: "How many patients could you recruit per year?", type: "numeric", hasSpecify: false },
  { key: "q_recruit_problems", no: "3",  section: 1,
    text: "Do you anticipate any problems recruiting eligible patients?",
    type: "yn", hasSpecify: true, specifyLabel: "If yes, specify:" },
  { key: "q_recruit_strategy", no: "4",  section: 1,
    text: "Do you have a patient recruitment strategy in place?",
    type: "yn", hasSpecify: true, specifyLabel: "If yes, describe:" },
  { key: "q_pat_per_year",     no: "5",  section: 1,
    text: "How many patients with the relevant condition do you see per year?",
    type: "numeric", hasSpecify: false },
  { key: "q_pat_eligible",     no: "6",  section: 1,
    text: "How many of these patients would potentially be eligible based on I/E criteria?",
    type: "numeric", hasSpecify: false },
  { key: "q_pat_currently",    no: "7",  section: 1,
    text: "How many eligible patients are currently being treated at your site?",
    type: "numeric", hasSpecify: false },

  // ── Domain 2: Operational Readiness ───────────────────────────────────────
  { key: "q_staff_available",   no: "8",  section: 2, sectionTitle: "Operational Readiness",
    text: "Do you have sufficient trained staff available for this study?",
    type: "yn", hasSpecify: true, specifyLabel: "If no, describe gaps:" },
  { key: "q_pi_commitment",     no: "9",  section: 2,
    text: "Is the PI able to commit adequate time to this study?",
    type: "yn", hasSpecify: true, specifyLabel: "If no, describe constraints:" },
  { key: "q_other_studies",     no: "10", section: 2,
    text: "Are you currently participating in other studies in this therapeutic area?",
    type: "yn", hasSpecify: true, specifyLabel: "If yes, specify:" },
  { key: "q_competing_studies", no: "11", section: 2,
    text: "How many competing studies are you running in the same indication?",
    type: "numeric", hasSpecify: false },
  { key: "q_study_overlap",     no: "12", section: 2,
    text: "Would any concurrent study overlap significantly with patient eligibility?",
    type: "yn", hasSpecify: true, specifyLabel: "If yes, explain:" },

  // ── Domain 3: Regulatory Compliance ──────────────────────────────────────
  { key: "q_gcp_training",   no: "13", section: 3, sectionTitle: "Regulatory Compliance",
    text: "Are all key staff current in GCP training?",
    type: "yn", hasSpecify: true, specifyLabel: "If no, specify who needs training:" },
  { key: "q_regulatory_exp", no: "14", section: 3,
    text: "Do you have experience with the required regulatory submissions?",
    type: "yn", hasSpecify: false },
  { key: "q_audit_history",  no: "15", section: 3,
    text: "Has your site had any significant findings in prior audits/inspections?",
    type: "yn", hasSpecify: true, specifyLabel: "If yes, describe findings:" },

  // ── Domain 4: Quality Track Record ────────────────────────────────────────
  { key: "q_dropout_rate",       no: "16", section: 4, sectionTitle: "Quality Track Record",
    text: "What is your historical patient dropout/withdrawal rate? (%)",
    type: "numeric", hasSpecify: false },
  { key: "q_protocol_deviation", no: "17", section: 4,
    text: "What is your average protocol deviation rate? (%)",
    type: "numeric", hasSpecify: false },
  { key: "q_prev_studies",       no: "18", section: 4,
    text: "How many clinical trials has this site conducted in the past 5 years?",
    type: "numeric", hasSpecify: false },

  // ── Domain 5: Facility & Equipment ────────────────────────────────────────
  { key: "q_infra_equipment", no: "19", section: 5, sectionTitle: "Facility & Equipment",
    text: "Does your site have the required equipment?",
    type: "yn", hasSpecify: true, specifyLabel: "If no, describe what is missing:" },
  { key: "q_infra_space",     no: "20", section: 5,
    text: "Is adequate space (exam rooms, pharmacy, storage) available?",
    type: "yn", hasSpecify: false },
];

// Display names for questionType, shared by the authoring table, its type <select>
// and the version viewer so the list can't drift between them.
export const QUESTION_TYPE_LABELS = {
  yn: "Yes / No",
  ynna: "Yes / No / NA",
  numeric: "Numeric",
  text: "Text",
  checklist: "Checklist",
};

// Types whose answer is a Y/N(/NA) button and that can carry a Specify follow-up.
export const YN_TYPES = ["yn", "ynna"];

// Answer codes each type offers, in display order.
export const YN_CODES = { yn: ["Y", "N"], ynna: ["Y", "N", "NA"] };

export const YN_LABEL = { Y: "Yes", N: "No", NA: "N/A" };

// A checklist answer is the JSON array of the checked options' keys, so two options
// sharing a key (or both missing one) are indistinguishable — checking one renders
// every one of them checked. The editor now assigns a key to every option, but rows
// authored before it did are still stored with `key: ""`, so blanks are filled here
// too. Index-based and deterministic, and identical to what QuestionRowsEditor writes
// on the next save, so healed rows keep matching the answers already recorded.
export function normalizeOptionKeys(options) {
  const list = Array.isArray(options) ? options : [];
  if (list.every((o) => o && typeof o.key === "string" && o.key.trim())) return list;

  const taken = new Set(list.map((o) => String((o && o.key) || "").trim()).filter(Boolean));
  let n = 0;
  return list.map((o) => {
    const key = String((o && o.key) || "").trim();
    if (key) return key === o.key ? o : { ...o, key };
    let next;
    do { next = `opt_${++n}`; } while (taken.has(next));
    taken.add(next);
    return { ...o, key: next };
  });
}

// Whether a checked checklist option offers a free-text follow-up. `hasSpecify` is the
// explicit switch the editor writes; options authored before it existed only have
// `specifyLabel`, which used to double as the switch — those keep showing the input.
// specifyLabel itself is now the input's placeholder, so it may be blank while on.
export function optionHasSpecify(opt) {
  if (!opt) return false;
  return opt.hasSpecify === undefined || opt.hasSpecify === null
    ? !!opt.specifyLabel
    : !!opt.hasSpecify;
}

// Convert DB question records (questionKey/questionText/…) to SECTIONS format
export function groupBySection(dbQuestions) {
  return dbQuestions
    .filter((q) => q.isActive !== false && q.isActive !== 0)
    .reduce((acc, q) => {
      const secNo = q.sectionNo;
      if (!acc[secNo]) {
        acc[secNo] = { title: q.sectionTitle || "", questions: [] };
      }
      const parsedOptions = q.options
        ? (typeof q.options === "string" ? JSON.parse(q.options) : q.options)
        : undefined;
      const options = parsedOptions ? normalizeOptionKeys(parsedOptions) : undefined;
      acc[secNo].questions.push({
        key:          q.questionKey,
        no:           q.questionNo,
        // null for a 상위 문항. Questions pinned before the hierarchy existed have no
        // parentKey at all, so they all read as top-level — which is what they were.
        parentKey:    q.parentKey || null,
        text:         q.questionText,
        type:         q.questionType,
        hasSpecify:   !!q.hasSpecify,
        specifyLabel: q.specifyLabel || undefined,
        specifyWhen:  q.specifyWhen || null,
        singleSelect: !!q.singleSelect,
        // Questions pinned into a version before the column existed have no
        // isRequired field — those count as required.
        isRequired:   q.isRequired !== false && q.isRequired !== 0,
        options,
      });
      return acc;
    }, {});
}

// ── 상위 / 하위 문항 ──────────────────────────────────────────────────────────
//
// A question whose `parentKey` names another question's key is that question's
// 하위 문항. The hierarchy is two levels deep and no more, which is what lets the
// numbering, the Show Q scope and the drag rules all be decided without recursion.
// Enforced client-side by QuestionRowsEditor.normalizeHierarchy and server-side by
// services/Feasibility/questionStructure.js.
//
// Both question shapes are accepted here, the same way questionLabel accepts both:
// editor rows carry questionKey/questionNo, renderer rows (groupBySection) carry
// key/no. Everything below reads through these two accessors.

export const keyOf = (q) => (q ? (q.key ?? q.questionKey) : undefined);
export const numOf = (q) => (q ? (q.no ?? q.questionNo) : undefined);

// questionKey -> question, over any mix of the two shapes.
export function indexByKey(questions) {
  const byKey = {};
  for (const q of questions || []) {
    const key = keyOf(q);
    if (key) byKey[key] = q;
  }
  return byKey;
}

// 문항 목록에서 번호 자리에 찍는 것: 상위 문항은 자기 번호, 하위 문항은 "┖ 1".
//
// 상위 문항이 바로 위에 있고 들여쓰기까지 있는 자리 — No. 칸, 문항 목록, Show Q 고르기,
// 내보낸 문서 — 에서 쓴다. 거기서는 어느 상위에 딸렸는지 위치가 말해 주므로, 번호는
// 하위라는 표시와 자기 번호만 있으면 된다.
//
// 부모가 곁에 없는 자리에서 다른 문항을 가리켜 부를 때는 이것으로 모자란다("┖ 1"은 어느
// 상위의 하위인지 말하지 않는다) — 그쪽은 아래 noPath의 전체 경로를 쓴다.
export const BRANCH_MARK = "┖ ";

export function branchNo(q) {
  const own = numOf(q);
  return q && q.parentKey ? `${BRANCH_MARK}${own ?? ""}` : own;
}

// The number a question is displayed by within its section: "3" for a 상위 문항,
// "3-1" for a 하위 문항. A sub-question's own number restarts at 1 under every parent,
// so on its own it does not identify anything — the parent's number has to lead.
// An unresolvable parentKey degrades to the bare number rather than inventing a path.
//
// 이쪽은 문항을 목록 밖에서 이름으로 부를 때 쓴다 — questionLabel이 만드는
// "2-3-1. 질문 내용" (변경 내역, 선택 시 표시되는 질문 안내).
export function noPath(q, byKey) {
  const own = numOf(q);
  const parent = q && q.parentKey ? (byKey || {})[q.parentKey] : null;
  if (!parent) return own;
  const parentNo = numOf(parent);
  return parentNo == null || parentNo === "" ? own : `${parentNo}-${own}`;
}

// The questions a checklist option on `row` may reveal:
//   - 상위 문항 → every other 상위 문항, plus its own 하위 문항
//   - 하위 문항 → the other 하위 문항 under the same parent
//
// The scope exists because Show Q used to offer every question in the set: a toggle per
// question, growing without bound, with no way to see which combinations were reachable.
// Inside the hierarchy the offer is small enough to read.
//
// Note this constrains *authoring* only. computeHiddenKeys follows whatever
// showsQuestions holds, so a set authored before the scope existed keeps working.
export function showQCandidates(questions, row) {
  const rowKey = keyOf(row);
  const parentKey = row && row.parentKey;
  return (questions || []).filter((q) => {
    const key = keyOf(q);
    if (!key || key === rowKey) return false;
    return parentKey
      // 하위 문항: siblings only.
      ? q.parentKey === parentKey
      // 상위 문항: other top-level questions, plus this question's own children.
      : (!q.parentKey || q.parentKey === rowKey);
  });
}

// ── Conditional display + required answers ───────────────────────────────────
//
// Mirrored server-side in becco-cro-api/services/Feasibility/answerValidation.js
// (the submit backstop). Keep the two in sync.
//
// `sections` is the groupBySection/SECTIONS shape; `answers` is
// { questionKey: { answerYN, answerNumeric, answerText, specifyText } }.
//
// pruneHidden also clears specify text whose trigger no longer matches. That part has
// no server mirror on purpose — nothing prunes on autosave server-side, and stale text
// is instead gated out at render time (InvitationViewer, pdfExport, wordExport all run
// it through the same specifyMatches predicate).

function parseJsonSafe(raw, fallback) {
  try { return JSON.parse(raw || ""); } catch { return fallback; }
}

// Which answers make a Specify follow-up relevant. Stored as a comma-joined subset of
// Y/N/NA; empty or absent means "always shown", which is what every question authored
// before the column existed has.
export function parseSpecifyWhen(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  return String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
}

export function specifyMatches(specifyWhen, answerYN) {
  const when = parseSpecifyWhen(specifyWhen);
  if (!when.length) return true;
  return when.includes(answerYN || "");
}

// Renderer-shape convenience wrapper: is the Specify textarea showing for this answer?
export function isSpecifyShown(question, answer) {
  if (!question.hasSpecify || !YN_TYPES.includes(question.type)) return false;
  return specifyMatches(question.specifyWhen, answer && answer.answerYN);
}

function allQuestions(sections) {
  return Object.keys(sections)
    .sort((a, b) => Number(a) - Number(b))
    .flatMap((sec) => sections[sec].questions);
}

function checkedOptionKeys(answer) {
  const parsed = parseJsonSafe(answer && answer.answerText, []);
  return Array.isArray(parsed) ? parsed : [];
}

// A question named by some checklist option's showsQuestions is conditional: hidden
// unless a triggering option is checked AND the question holding that option is itself
// visible. Iterated to a fixpoint so a hidden checklist can't keep its children visible.
//
// A 하위 문항 is hidden whenever its 상위 문항 is — otherwise unchecking the option that
// reveals a parent would leave its sub-questions stranded on the form with nothing above
// them. That rule can't be folded into the loop: the loop only ever *removes* from the
// hidden set, so a rule that adds to it would fight the loop and oscillate. So the loop
// stays purely about conditions (`condHidden`) and parentage is applied on top, inside
// the one predicate both the loop and the result ask. Two levels, so never more than one
// hop to a parent.
//
// Mirrored in the API's services/Feasibility/answerValidation.js.
export function computeHiddenKeys(sections, answers) {
  const conditions = {}; // targetKey -> [{ sourceKey, optKey }]
  const parentOf = {};
  for (const q of allQuestions(sections)) {
    if (q.parentKey) parentOf[q.key] = q.parentKey;
    if (q.type !== "checklist" || !q.options) continue;
    for (const opt of q.options) {
      for (const targetKey of opt.showsQuestions || []) {
        if (!conditions[targetKey]) conditions[targetKey] = [];
        conditions[targetKey].push({ sourceKey: q.key, optKey: opt.key });
      }
    }
  }

  const condHidden = new Set(Object.keys(conditions));
  const isVisible = (key) =>
    !condHidden.has(key) && (!parentOf[key] || !condHidden.has(parentOf[key]));

  for (;;) {
    let changed = false;
    for (const targetKey of Object.keys(conditions)) {
      if (!condHidden.has(targetKey)) continue;
      const visible = conditions[targetKey].some(
        ({ sourceKey, optKey }) =>
          isVisible(sourceKey) && checkedOptionKeys(answers[sourceKey]).includes(optKey)
      );
      if (visible) {
        condHidden.delete(targetKey);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const hidden = new Set(condHidden);
  for (const q of allQuestions(sections)) {
    if (!isVisible(q.key)) hidden.add(q.key);
  }
  return hidden;
}

// The main answer only — Specify follow-ups are never required. 'NA' counts as an
// answer for ynna: it's a deliberate choice, and a required ynna question would
// otherwise be unsubmittable.
export function isAnswered(question, answer) {
  if (!answer) return false;
  switch (question.type) {
    case "yn":
      return answer.answerYN === "Y" || answer.answerYN === "N";
    case "ynna":
      return answer.answerYN === "Y" || answer.answerYN === "N" || answer.answerYN === "NA";
    case "numeric":
      return answer.answerNumeric != null && answer.answerNumeric !== "";
    case "checklist":
      return checkedOptionKeys(answer).length > 0;
    case "text":
    default:
      return typeof answer.answerText === "string" && answer.answerText.trim() !== "";
  }
}

// SFQ_Answer.answerNumeric is DECIMAL(10,2) — eight integer digits and two decimals.
// Mirrors the binding in the API's services/Feasibility/sitePublic.js. Sending a value
// past the column aborts the autosave transaction, and that transaction carries the
// whole batch: one over-long number silently loses every other answer saved alongside
// it. So the form refuses the value instead of queueing it.
export const NUMERIC_MAX = 99999999.99;
export const NUMERIC_SCALE = 2;
export const NUMERIC_MAX_LABEL = "99,999,999.99";

// The site form shows these to a respondent who may be reading the page in English, so the
// message follows the page's language. Internal screens pass nothing and stay Korean.
const NUMERIC_ERRORS = {
  ko: {
    notANumber: "숫자만 입력할 수 있습니다.",
    tooBig:     `최대 ${NUMERIC_MAX_LABEL}까지 입력할 수 있습니다.`,
    tooPrecise: `소수점 이하 ${NUMERIC_SCALE}자리까지 입력할 수 있습니다.`,
  },
  en: {
    notANumber: "Only numbers can be entered here.",
    tooBig:     `The largest value that can be entered is ${NUMERIC_MAX_LABEL}.`,
    tooPrecise: `Up to ${NUMERIC_SCALE} decimal places can be entered.`,
  },
};

// "" is how a numeric answer is cleared, so it is not an error here. Returns the message
// to show, or "" when the value is storable as-is.
export function numericRangeError(raw, lang = "ko") {
  const str = String(raw ?? "").trim();
  if (str === "") return "";

  const msg = NUMERIC_ERRORS[lang] || NUMERIC_ERRORS.ko;
  const num = Number(str);
  if (!Number.isFinite(num)) return msg.notANumber;
  if (Math.abs(num) > NUMERIC_MAX) return msg.tooBig;
  // Round-tripping through toFixed catches an over-precise value whatever notation it
  // arrived in — a number input accepts "1.005" and "1e-9" alike, and both would be
  // rounded away by the column without the respondent ever seeing it happen.
  if (Number(num.toFixed(NUMERIC_SCALE)) !== num) return msg.tooPrecise;
  return "";
}

// True when the answer holds any value at all — decides whether a question that just
// became hidden still has data worth clearing.
function hasAnyValue(answer) {
  if (!answer) return false;
  return (
    (answer.answerYN != null && answer.answerYN !== "") ||
    (answer.answerNumeric != null && answer.answerNumeric !== "") ||
    (answer.answerText != null && answer.answerText !== "") ||
    (answer.specifyText != null && answer.specifyText !== "")
  );
}

export const CLEARED_ANSWER = {
  answerYN: null, answerNumeric: null, answerText: null, specifyText: null,
};

// Wipe answers to questions that are no longer visible, so an unchecked trigger can't
// leave a ghost answer behind in scoring or the CRA report. Clearing a checklist answer
// can hide its own children, so this repeats until the visible set settles.
//
// Then a second, single pass over the still-visible questions drops specify text whose
// trigger no longer matches — switching Yes→No on an "if yes, describe" question must
// not leave the description behind. That one keeps the rest of the answer, so it is
// reported separately: `clearedKeys` is consumed as an all-null CLEARED_ANSWER patch and
// would wipe the answerYN the respondent just picked.
//
// Returns the new map plus both sets of keys (they must be pushed to the server).
export function pruneHidden(sections, answers) {
  let next = answers;
  const clearedKeys = [];

  for (;;) {
    const current = next;
    const hidden = computeHiddenKeys(sections, current);
    const toClear = [...hidden].filter((key) => hasAnyValue(current[key]));
    if (!toClear.length) break;

    next = { ...current };
    for (const key of toClear) {
      next[key] = { ...current[key], ...CLEARED_ANSWER };
      clearedKeys.push(key);
    }
  }

  // Idempotent and non-recursive: nulling specifyText can't change what's visible, so
  // this runs once, outside the loop above.
  const hidden = computeHiddenKeys(sections, next);
  const specifyClearedKeys = allQuestions(sections)
    .filter((q) => !hidden.has(q.key) && q.hasSpecify && YN_TYPES.includes(q.type))
    .filter((q) => {
      const answer = next[q.key];
      // A checklist keeps a JSON option→text map in the same column; YN_TYPES already
      // excludes those, so anything here is plain text.
      return answer && answer.specifyText &&
        !specifyMatches(q.specifyWhen, answer.answerYN);
    })
    .map((q) => q.key);

  if (specifyClearedKeys.length) {
    next = { ...next };
    for (const key of specifyClearedKeys) {
      next[key] = { ...next[key], specifyText: null };
    }
  }

  return { answers: next, clearedKeys, specifyClearedKeys };
}

// Visible + required + unanswered questions, in display order.
export function findMissingRequired(sections, answers) {
  const hidden = computeHiddenKeys(sections, answers);
  return allQuestions(sections).filter(
    (q) => !hidden.has(q.key) && q.isRequired !== false && !isAnswered(q, answers[q.key])
  );
}

// Group by section for rendering
export const SECTIONS = QUESTIONS.reduce((acc, q) => {
  if (!acc[q.section]) {
    acc[q.section] = {
      section: q.section,
      title: q.sectionTitle || "",
      questions: [],
    };
  }
  acc[q.section].questions.push(q);
  return acc;
}, {});

// SFQ (Study) statuses
export const STATUS_LABELS = {
  Draft:          "Draft",
  InternalReview: "Internal Review",
  Active:         "Active",
  Finalized:      "Finalized",
  Locked:         "Locked",
  Archived:       "Archived",
};

export const STATUS_COLOR = {
  Draft:          "#6c757d",
  InternalReview: "#fd7e14",
  Active:         "#0d6efd",
  Finalized:      "#198754",
  Locked:         "#495057",
  Archived:       "#adb5bd",
};

// Finalize closes a study out: from there on it is read-only. Editing, sending sites,
// scoring, the people it names — all of it stops, and the only thing left is the
// lifecycle itself (Finalize → Lock → Archive, and the rollback). Reads and PDF/Word
// exports stay open. Mirrors FROZEN_STATUSES in the API's services/Feasibility/index.js,
// which is what actually enforces it (422); this only decides what to put on screen.
export const FROZEN_STATUSES = ["Finalized", "Locked", "Archived"];
export const isStudyFrozen = (status) => FROZEN_STATUSES.includes(status);

// ── Submit for Review gate ───────────────────────────────────────────────────
//
// A study only goes to the reviewer with something to review: the narrative and at
// least one question. Mirrored server-side in services/Feasibility/index.js (the
// Draft → InternalReview backstop, which is what actually enforces it); this decides
// whether the button is offered.

// The editor's "empty" is not "": ProseMirror keeps an empty paragraph behind, so the
// tags come off before the check. Embedded media carries no text but is content.
export function hasNarrativeContent(html) {
  const raw = String(html || "");
  if (/<(img|table|hr|iframe)\b/i.test(raw)) return true;
  return raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim() !== "";
}

// Tab names of what a Draft is still missing, in tab order. Empty = ready to submit.
// `questions` is the working copy's question rows (Redux `sfqQuestions`), so unsaved
// edits in the Questions tab do not count — the reviewer sees what was saved.
export function findSubmitBlockers(sfq, questions) {
  const blockers = [];
  if (!hasNarrativeContent(sfq && sfq.narrativeHtml)) blockers.push("Study Overview");
  if (!(Array.isArray(questions) && questions.length)) blockers.push("Questions");
  return blockers;
}

// SFQ_SiteInvitation statuses
export const INV_STATUS_LABELS = {
  Pending:   "Pending",
  Sent:      "Sent",
  InProgress:"In Progress",
  Submitted: "Submitted",
  Reviewed:  "Reviewed",
  Finalized: "Finalized",
};

export const RISK_COLOR = {
  Low:      "#198754",
  Medium:   "#fd7e14",
  High:     "#dc3545",
  Critical: "#6f1a1a",
};

// Score domain definitions (CRA가 수동 입력하는 도메인 점수; weight는 참고 표기용)
export const SCORE_DOMAINS = [
  // { key: "scoreEnrollment",  label: "Enrollment",  weight: "30%" },
  // { key: "scoreOperational", label: "Operational", weight: "25%" },
  // { key: "scoreRegulatory",  label: "Regulatory",  weight: "20%" },
  // { key: "scoreQuality",     label: "Quality",     weight: "15%" },
  // { key: "scoreFacility",    label: "Facility",    weight: "10%" },
  { key: "scoreEnrollment",  label: "Enrollment"  },
  { key: "scoreOperational", label: "Operational" },
  { key: "scoreRegulatory",  label: "Regulatory"  },
  { key: "scoreQuality",     label: "Quality"     },
  { key: "scoreFacility",    label: "Facility"    },
];
